import axios from 'axios'

import authService from './services/auth.service'

const instance = axios.create({
  baseURL: process.env.REACT_APP_API,
  timeout: 1200000,
  withCredentials: false,
})

const ORDER_RETRY_ATTEMPTS = 3

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const shouldRetryAssignmentOrderRequest = (error) => {
  if (!error) return false

  // Network / transport issues (no HTTP response came back).
  if (!error.response) return true

  const status = Number(error?.response?.status)
  if (status >= 500 || status === 408 || status === 409 || status === 429) {
    return true
  }

  const message = String(error?.message || '').toLowerCase()
  return (
    message.includes('timeout') ||
    message.includes('network error') ||
    message.includes('econnreset') ||
    message.includes('etimedout') ||
    message.includes('deadlock') ||
    message.includes('lock wait timeout')
  )
}

const postWithRetry = async (
  url,
  data,
  { attempts = ORDER_RETRY_ATTEMPTS } = {}
) => {
  let lastError
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await instance.post(url, data)
    } catch (error) {
      lastError = error
      const canRetry =
        attempt < attempts && shouldRetryAssignmentOrderRequest(error)
      if (!canRetry) break
      const delayMs = 250 * attempt * attempt
      await sleep(delayMs)
    }
  }
  throw lastError
}

// Add a request interceptor
instance.interceptors.request.use(
  function (config) {
    // Do something before request is sent
    const isLoggedIn =
      authService?.isLoggedIn() || authService?.getSignUpToken()
    if (isLoggedIn) {
      if (authService?.isLoggedIn())
        config.headers['Authorization'] = `Bearer ${authService.token()}`
      if (authService?.getSignUpToken()) {
        config.headers['Authorization'] =
          `Bearer ${authService.getSignUpToken()}`
      }
    }
    return config
  },
  function (error) {
    // Do something with request errors
    console.error('API Error:', error.response.data)
    return Promise.reject(error)
  }
)

export const register = async (data) => {
  return axios.post(
    `${process.env.REACT_APP_AUTH}/api/auth/local/register`,
    data
  )
}

export const signUp = async (data) => {
  return instance.post('/verifies/admin-signup', data)
}

export const login = async (data) => {
  return axios.post(`${process.env.REACT_APP_AUTH}/api/auth/local`, {
    ...data,
    client: 'admin',
  })
}

export const requestPasswordReset = async (email) => {
  return axios.post(`${process.env.REACT_APP_AUTH}/api/auth/forgot-password`, {
    email,
  })
}

export const resetPasswordByCode = async ({
  code,
  password,
  passwordConfirmation,
}) => {
  return axios.post(`${process.env.REACT_APP_AUTH}/api/auth/reset-password`, {
    code,
    password,
    passwordConfirmation,
  })
}

export const getMyOrganizations = async () => {
  return instance.get('/organizations/myOrgs')
}

export const getOrganizationSessionContext = async () => {
  return instance.get('/organizations/session-context')
}

export const logReportAccess = async ({
  organizationUUID,
  reportId,
  reportTitle,
  reportPath,
}) => {
  return instance.post('/report-access-log/log', {
    organizationUUID,
    reportId,
    reportTitle,
    reportPath,
  })
}

export const getReportAccessLogs = async (
  organizationUUID,
  { page = 1, perPage = 50 } = {}
) => {
  return instance.get(
    `/report-access-log/list?organizationUUID=${encodeURIComponent(organizationUUID)}&page=${page}&perPage=${perPage}`
  )
}

export const reportByDateRange = async (
  organizationUUID,
  reportType,
  from,
  to,
  signal
) => {
  return instance.get(
    `/report/report-by-date-range?organizationUUID=${organizationUUID}&reportType=${reportType}&from=${from}&to=${to}`,
    signal ? { signal } : undefined
  )
}

export const getAccessControlSummary = async (organizationUUID, eventUUID) => {
  return instance.get(
    `/report/get-access-control-summary?organizationUUID=${organizationUUID}&eventUUID=${eventUUID}`
  )
}

export const getEarlyScanExplanation = async (organizationUUID, eventUUID) => {
  return instance.get(
    `/report/get-early-scan-explanation?organizationUUID=${organizationUUID}&eventUUID=${eventUUID}`
  )
}

export const getEventSalesAndAttendance = async (
  organizationUUID,
  from,
  to
) => {
  return instance.get(
    `/report/get-event-sales-and-attendance?organizationUUID=${organizationUUID}&from=${from}&to=${to}`
  )
}

const compIntelligenceQuery = (organizationUUID, filters = {}) => {
  const params = new URLSearchParams({ organizationUUID })
  if (filters.eventUuid) params.set('eventUuid', filters.eventUuid)
  if (filters.programId) params.set('programId', filters.programId)
  if (filters.source) params.set('source', filters.source)
  if (filters.start) params.set('start', filters.start)
  if (filters.end) params.set('end', filters.end)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize))
  return params.toString()
}

export const getCompIntelligenceOverview = async (
  organizationUUID,
  filters
) => {
  return instance.get(
    `/comp-intelligence/overview?${compIntelligenceQuery(organizationUUID, filters)}`
  )
}

export const getCompIntelligenceEvent = async (organizationUUID, eventUuid) => {
  return instance.get(
    `/comp-intelligence/events/${eventUuid}?organizationUUID=${organizationUUID}`
  )
}

export const getCompIntelligenceAllocations = async (
  organizationUUID,
  filters
) => {
  return instance.get(
    `/comp-intelligence/allocations?${compIntelligenceQuery(organizationUUID, filters)}`
  )
}

export const getCompIntelligenceRecipients = async (
  organizationUUID,
  filters
) => {
  return instance.get(
    `/comp-intelligence/recipients?${compIntelligenceQuery(organizationUUID, filters)}`
  )
}

export const refreshCompIntelligence = async (organizationUUID, eventUuid) => {
  const params = new URLSearchParams({ organizationUUID })
  if (eventUuid) params.set('eventUuid', eventUuid)
  return instance.post(`/comp-intelligence/refresh?${params.toString()}`)
}

export const exportCompIntelligence = async (organizationUUID, filters) => {
  return instance.get(
    `/comp-intelligence/export?${compIntelligenceQuery(organizationUUID, filters)}`,
    { responseType: 'blob' }
  )
}

export const getUserBalanceReport = async (organizationUUID, from, to) => {
  return instance.get(
    `/report/user-balance?organizationUUID=${organizationUUID}&from=${from}&to=${to}`
  )
}

export const getPaymentListings = async (organizationUUID, from, to) => {
  return instance.get(
    `/report/get-payment-listings?organizationUUID=${organizationUUID}&from=${from}&to=${to}`
  )
}

export const getPackagesReport = async (organizationUUID, from, to) => {
  return instance.get(
    `/report/get-packages-report?organizationUUID=${organizationUUID}&from=${from}&to=${to}`
  )
}

export const getFlexPackageSalesSummary = async (
  organizationUUID,
  seasonId
) => {
  return instance.get(
    `/report/get-flex-package-sales-summary?organizationUUID=${organizationUUID}&seasonId=${seasonId}`
  )
}

export const getSalesReceivablesByCategory = async (
  organizationUUID,
  seasonId,
  from,
  to
) => {
  const params = new URLSearchParams({
    organizationUUID,
    seasonId: String(seasonId),
  })
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  return instance.get(
    `/report/get-sales-receivables-by-category?${params.toString()}`
  )
}

export const getBuyerTypesReport = async (
  organizationUUID,
  { seasonId, from, to }
) => {
  const params = new URLSearchParams({ organizationUUID })
  if (seasonId) {
    params.set('seasonId', String(seasonId))
  } else {
    if (from) params.set('from', from)
    if (to) params.set('to', to)
  }
  return instance.get(`/report/get-buyer-types-report?${params.toString()}`)
}

export const getAgedReceivableReport = async (organizationUUID, from, to) => {
  return instance.get(
    `/report/get-aged-receivables?organizationUUID=${organizationUUID}&from=${from}&to=${to}`
  )
}

export const getARAgingSummaryReport = async (organizationUUID, asOfDate) => {
  return instance.get(
    `/report/report-by-date-range?organizationUUID=${organizationUUID}&reportType=ar-aging-summary&from=${asOfDate}&to=${asOfDate}`
  )
}

export const getARAgingDetailReport = async (organizationUUID, asOfDate) => {
  return instance.get(
    `/report/report-by-date-range?organizationUUID=${organizationUUID}&reportType=ar-aging-detail&from=${asOfDate}&to=${asOfDate}`
  )
}

export const getTimeStampReport = async (organizationUUID, eventUUID, from) => {
  return instance.get(
    `/report/get-time-stamp-report?organizationUUID=${organizationUUID}&eventUUID=${eventUUID}&from=${from}`
  )
}

export const getClubSeatsReport = async (seasonId) => {
  return instance.get(`/report/club-seats?seasonId=${seasonId}`)
}

export const getReportBuilderFieldCatalog = async () => {
  return instance.get('/report-builder/field-catalog')
}

export const previewReportBuilder = async (data, signal) => {
  return instance.post(
    '/report-builder/preview',
    data,
    signal ? { signal } : undefined
  )
}

export const exportReportBuilder = async (data, signal) => {
  return instance.post('/report-builder/export', data, {
    responseType: 'blob',
    ...(signal ? { signal } : {}),
  })
}

export const getReportBuilderReports = async (organizationUUID) => {
  return instance.get(
    `/report-builder/reports?organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const getReportBuilderReport = async (reportId, organizationUUID) => {
  return instance.get(
    `/report-builder/reports/${reportId}?organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const createReportBuilderReport = async (data) => {
  return instance.post('/report-builder/reports', data)
}

export const updateReportBuilderReport = async (reportId, data) => {
  return instance.put(`/report-builder/reports/${reportId}`, data)
}

export const deleteReportBuilderReport = async (reportId, organizationUUID) => {
  return instance.delete(
    `/report-builder/reports/${reportId}?organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const runReportBuilderReport = async (
  reportId,
  organizationUUID,
  signal
) => {
  return instance.get(
    `/report-builder/reports/${reportId}/run?organizationUUID=${encodeURIComponent(organizationUUID)}`,
    signal ? { signal } : undefined
  )
}

export const getOrgVenues = async (organizationUUID, options = {}) => {
  const params = new URLSearchParams()
  if (organizationUUID != null && String(organizationUUID).length > 0) {
    params.set('organizationUUID', organizationUUID)
  }
  if (options.includeMapping) params.set('includeMapping', 'true')
  const query = params.toString()
  return instance.get(`/organizations/venues${query ? `?${query}` : ''}`)
}

export const getMyPermissions = async () => {
  return instance.get('/organizations/my-permissions')
}

export const getMe = async (token) => {
  instance.interceptors.request.use(function (config) {
    config.headers['Authorization'] = `Bearer ${token}`
    return config
  })

  return instance.get(
    '/users/me?populate[organizations][populate][0]=members&populate[organizations][populate][1]=organization_fee_config&populate[organizations][populate][2]=image&populate[organizations][populate][3]=taxRate&populate[organizations][populate][4]=setting&populate[organizations][populate][5]=email_logo&populate[organizations][populate][branding][populate][0]=logo&populate[organizations][populate][branding][populate][1]=darkLogo&populate[role][fields][0]=name&populate[organization_role][populate][organization_permissions][fields][0]=name&populate[organization_role][populate][organization_permissions][fields][1]=key&populate[organization_role][populate][organization_permissions][fields][2]=slug&populate[profile][populate][pos_terminal][fields][0]=location&populate[profile][populate][pos_terminal][fields][1]=label&populate[profile][populate][pos_terminal][fields][2]=stripeReaderId'
  )
}

export const getTeam = async (organizationUUID) => {
  const suffix =
    organizationUUID != null && String(organizationUUID).length > 0
      ? `?organizationUUID=${encodeURIComponent(organizationUUID)}`
      : ''
  return instance.get(`/organizations/team${suffix}`)
}

export const getTeamMembers = async () => {
  return instance.get('/organizations/get-team-members')
}

export const getBoxOfficeAccountReps = async (organizationUUID) => {
  return instance.get(
    `/organizations/get-box-office-account-reps?organizationUUID=${organizationUUID}`
  )
}

export const getAutomaticReports = async (uuid) => {
  return instance.get(`/organizations/get-automatic-reports?eventUUID=${uuid}`)
}

export const addMember = async (data) => {
  return instance.post('/organizations/add-member', data)
}

export const createDiscount = async (data) => {
  return instance.post('/offer-discount/create-discount', data)
}

export const createEventReport = async (data) => {
  return instance.post('/organizations/create-event-report', data)
}

export const updateOrgDetails = async (data) => {
  return instance.post('/organizations/update-details', data)
}

export const getOrgEmailTemplates = async (organizationUUID) => {
  return instance.get('/organizations/email-templates', {
    params: { organizationUUID },
  })
}

export const updateOrgEmailTemplates = async (data) => {
  return instance.put('/organizations/email-templates', data)
}

export const sendOrgEmailTemplateTest = async (data) => {
  return instance.post('/organizations/email-templates/send-test', data)
}

export const generateOrgEmailTemplateCopy = async (data) => {
  return instance.post('/organizations/email-templates/generate-copy', data)
}

// email is not taken by another user
export const emailValid = async (data) => {
  return instance.post('/organizations/email-valid', data)
}

// email is a known email address
export const validEmail = (data) => {
  return instance.post('/verifies/emailValid', data)
}

export const updateUserEmail = async (data) => {
  return instance.post('/organizations/update-user-email', data)
}

export const updateUsersName = async (data) => {
  return instance.post('/organizations/update-users-name', data)
}

export const getUiPreferences = async () => {
  return instance.get('/organizations/ui-preferences')
}

export const updateOrderListingsColumnTemplates = async (
  orderListingsColumnTemplates
) => {
  return instance.put('/organizations/order-listings-column-templates', {
    orderListingsColumnTemplates,
  })
}

export const listOrderListingsReportSubscriptions = async (
  organizationUUID
) => {
  return instance.get(
    `/order-listings-report-subscriptions?organizationUUID=${encodeURIComponent(
      organizationUUID
    )}`
  )
}

export const saveOrderListingsReportSubscription = async (payload) => {
  return instance.post('/order-listings-report-subscriptions', {
    data: payload,
  })
}

export const deleteOrderListingsReportSubscription = async (
  id,
  organizationUUID
) => {
  return instance.delete(
    `/order-listings-report-subscriptions/${encodeURIComponent(
      id
    )}?organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const sendOrderListingsReportTestEmail = async (payload) => {
  return instance.post('/order-listings-report-subscriptions/send-test', {
    data: payload,
  })
}

export const listPromoterSalesReportSubscriptions = async (
  organizationUUID
) => {
  return instance.get(
    `/promoter-sales-report-subscriptions?organizationUUID=${encodeURIComponent(
      organizationUUID
    )}`
  )
}

export const savePromoterSalesReportSubscription = async (payload) => {
  return instance.post('/promoter-sales-report-subscriptions', {
    data: payload,
  })
}

export const deletePromoterSalesReportSubscription = async (
  id,
  organizationUUID
) => {
  return instance.delete(
    `/promoter-sales-report-subscriptions/${encodeURIComponent(
      id
    )}?organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const sendPromoterSalesReportTestEmail = async (payload) => {
  return instance.post('/promoter-sales-report-subscriptions/send-test', {
    data: payload,
  })
}

export const listSummaryByPaymentMethodReportSubscriptions = async (
  organizationUUID
) => {
  return instance.get(
    `/summary-by-payment-method-report-subscriptions?organizationUUID=${encodeURIComponent(
      organizationUUID
    )}`
  )
}

export const saveSummaryByPaymentMethodReportSubscription = async (payload) => {
  return instance.post('/summary-by-payment-method-report-subscriptions', {
    data: payload,
  })
}

export const deleteSummaryByPaymentMethodReportSubscription = async (
  id,
  organizationUUID
) => {
  return instance.delete(
    `/summary-by-payment-method-report-subscriptions/${encodeURIComponent(
      id
    )}?organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const sendSummaryByPaymentMethodReportTestEmail = async (payload) => {
  return instance.post(
    '/summary-by-payment-method-report-subscriptions/send-test',
    {
      data: payload,
    }
  )
}

export const listPayoutReportTemplates = async (organizationUUID) => {
  return instance.get(
    `/payout-report-templates?organizationUUID=${encodeURIComponent(
      organizationUUID
    )}`
  )
}

export const savePayoutReportTemplate = async (payload) => {
  return instance.post('/payout-report-templates', {
    data: payload,
  })
}

export const deletePayoutReportTemplate = async (id, organizationUUID) => {
  return instance.delete(
    `/payout-report-templates/${encodeURIComponent(
      id
    )}?organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const resetPassword = async (data) => {
  return instance.post('/organizations/update-users-password', data)
}

export const createOrganization = async (data) => {
  return instance.post('/verifies/admin-create-org', data)
}

export const getOrganizationRoles = async (organizationUUID) => {
  const params =
    organizationUUID != null && String(organizationUUID).length > 0
      ? { organizationUUID }
      : undefined
  return instance.get('/organization-roles/roles', { params })
}

/** Strapi defaults to a small page size (~25); catalog exceeds one page, so report rows were omitted. */
export const getOrganizationPermissions = async () => {
  return instance.get('/organization-permissions', {
    params: {
      'pagination[pageSize]': 200,
      'pagination[page]': 1,
    },
  })
}

export const createOrEditRole = async (data) => {
  return instance.post('/organization-roles/create', data)
}

export const createPaymentInfo = async (data) => {
  return instance.post('/organizations/create-payment-info', data)
}

export const getPaymentInfo = async () => {
  return instance.get(`/organizations/payment-info`)
}

export const createW9 = async (data) => {
  return instance.post('/organizations/create-w9', data)
}

export const createPromoLink = async (data) => {
  return instance.post('/organizations/create-promo', data)
}

export const editPromoLink = async (data) => {
  return instance.post('/organizations/edit-promo', data)
}

export const createOrEditMember = async (data) => {
  return instance.post('/organizations/invite-member', data)
}

export const removeTeamMember = async (data) => {
  return instance.post('/organizations/remove-member', data)
}

export const removeRoles = async (data) => {
  return instance.post('/organization-roles/remove', data)
}

export const removeBankAccount = async (data) => {
  return instance.post('/organizations/remove-bankaccount', data)
}

export const getCategories = async () => {
  return instance.get('/categories')
}

export const getVenues = async () => {
  return instance.get('/venues')
}

export const getPromoStats = async (uuid) => {
  return instance.get(`/organizations/promo-stats?uuid=${uuid}`)
}

export const getEventTicketTypes = async (uuid) => {
  return instance.get(`/organizations/event-ticket-types?uuid=${uuid}`)
}

export const attendeesList = async (uuid) => {
  return instance.get(`/attendees-list/event?uuid=${uuid}`)
}

export const getW9 = async (uuid) => {
  return instance.get(`/organizations/w9`)
}

export const guestList = async (uuid) => {
  return instance.get(`/organizations/guest-list?uuid=${uuid}`)
}

export const getEventWaitlist = async (eventUuid) => {
  return instance.get(`/waitlist?eventUuid=${eventUuid}`)
}

export const exportEventPurchasers = async (eventUuid) => {
  return instance.get(`/events/${eventUuid}/purchaser-export`, {
    responseType: 'blob',
  })
}

export const removeWaitlistEntry = async (id) => {
  return instance.delete(`/waitlist/${id}`)
}

export const createEvent = async (data) => {
  return instance.post('/events', data)
}

export const removeEvent = async (uuid) => {
  return instance.delete(`/events/remove-event/${uuid}`)
}

export const createGuestList = async (data) => {
  return instance.post('/organizations/create-guest-list', data)
}

export const removeGuestList = async (data) => {
  return instance.post('/organizations/remove-guest-list', data)
}

export const editEvent = async (data) => {
  return instance.put('/events/update-event', data)
}

export const addDetailsToEvent = async (data) => {
  return instance.post('/organizations/add-details', data)
}

export const getEvents = async () => {
  return instance.get('/organizations/events')
}

export const getEvent = async (uuid, options = {}) => {
  const params = new URLSearchParams({ uuid })
  if (options.view) params.set('view', options.view)
  else if (options.summary) params.set('summary', '1')
  return instance.get(`/organizations/event?${params.toString()}`)
}

export const getEventAssignmentMap = async (uuid) => {
  return instance.get(`/organizations/event-assignment-map?uuid=${uuid}`)
}

export const getEventSeatDetail = async (eventUuid, seatId, signal) => {
  const params = new URLSearchParams({
    eventUuid,
    seatId: String(seatId),
  })
  return instance.get(`/organizations/event-seat-detail?${params.toString()}`, {
    signal,
  })
}

export const getAssignableInventory = async (organizationUUID, seasonId) => {
  let url = `/organizations/assignable-inventory?organizationUUID=${encodeURIComponent(organizationUUID)}`
  if (seasonId) {
    url += `&seasonId=${encodeURIComponent(seasonId)}`
  }
  return instance.get(url)
}

export const getEventStats = async (uuid, range = '') => {
  return instance.get(`/organizations/event-stats?uuid=${uuid}&range=${range}`)
}

export const getAllEventStats = async (data, signal) => {
  return instance.post(`/organizations/all-event-stats`, data, {
    signal,
    timeout: 20000,
  })
}

export const bulkUpdateEventSettings = async (data) => {
  return instance.post(`/events/bulk-settings`, { data })
}

export const getBulkSettingsCandidates = async (data, signal) => {
  return instance.post(
    `/events/bulk-settings/candidates`,
    { data },
    signal ? { signal } : undefined
  )
}

export const getOrders = async (uuid, query, ordersView) => {
  return instance.get(
    `/organizations/event-orders?uuid=${uuid}&query=${query}&ordersView=${ordersView}`
  )
}

export const getAttendees = async (uuid) => {
  return instance.get(`/organizations/event-attendees?eventUUID=${uuid}`)
}

export const getTaxRates = async (city, state) => {
  return instance.get(`/organizations/tax-rates?city=${city}&state=${state}`)
}

export const publishEvent = async (data) => {
  return instance.post('/events/publish', data)
}

export const updateEventStatus = async (data) => {
  return instance.put('/events/update-event-status', data)
}

export const updateEventVisibility = async (data) => {
  return instance.put('/events/update-event-visibility', data)
}

export const updateEventIsVisible = async (data) => {
  return instance.put('/events/update-event-is-visible', data)
}

export const createTickets = async (data) => {
  return instance.post('/tickets', data)
}

export const updateTickets = async (data) => {
  return instance.post('/tickets/update-all', data)
}

export const inactivateTickets = async (data) => {
  return instance.post('/tickets/inactivate', data)
}

export const setTicketWasPrinted = async (data) => {
  return instance.post('/tickets/set-ticket-was-printed', data)
}

export const upload = async (data) => {
  return instance.post('/upload/', data)
}

export const getTicketFees = async (data) => {
  return instance.post('/organizations/get-ticket-fees', data)
}

export const getTicketDetails = async (uuid) => {
  return instance.get(`/organizations/all-ticket-details?uuid=${uuid}`)
}

export const getCampaigns = async (uuid) => {
  return instance.get(`/organizations/campaigns?uuid=${uuid}`)
}

export const getMarketingOptIns = async (organizationUUID) => {
  return instance.get('/organizations/marketing-opt-ins', {
    params: { organizationUUID },
  })
}

export const removeMarketingOptIn = async (data) => {
  return instance.delete('/organizations/marketing-opt-ins', {
    params: data,
  })
}

export const getPromoEmails = async (organizationUUID) => {
  return instance.get('/organizations/promo-emails', {
    params: { organizationUUID },
  })
}

export const sendPromoEmail = async (data) => {
  return instance.post('/organizations/promo-email', data)
}

export const listFundraisingCampaigns = async (organizationUUID) => {
  return instance.get(
    `/organizations/fundraising-campaigns?organizationUUID=${organizationUUID}`
  )
}

export const getFundraisingCampaign = async (
  organizationUUID,
  campaignUuid
) => {
  return instance.get(
    `/organizations/fundraising-campaigns/${campaignUuid}?organizationUUID=${organizationUUID}`
  )
}

export const saveFundraisingCampaign = async (data) => {
  return instance.post('/organizations/fundraising-campaign', data)
}

export const publishFundraisingCampaign = async (data) => {
  return instance.post('/organizations/fundraising-campaign/publish', data)
}

export const removeCampaign = async (id) => {
  return instance.delete(`/organizations/remove-campaign/${id}`)
}

export const removeAutomaticReport = async (id) => {
  return instance.delete(`/organizations/remove-automtic-report/${id}`)
}

export const removeOffer = async (id) => {
  return instance.delete(`/organizations/remove-offer/${id}`)
}

export const inviteValid = async (code) => {
  return instance.get(`/organizations/invite-valid?code=${code}`)
}

export const ticketsByGroup = async (groupCode, eventUUID) => {
  return instance.get(
    `/organizations/tickets-by-group?ticketGroup=${groupCode}&eventUUID=${eventUUID}`
  )
}

export const getAttendeesList = async (uuid) => {
  return instance.get(`/attendees-list/event?uuid=${uuid}`)
}

export const checkIn = async (data) => {
  return instance.post(`/attendees-list/checkInGuest`, data)
}

export const searchAttendees = async (uuid, query) => {
  return instance.get(`/attendees-list/search?uuid=${uuid}&query=${query}`)
}

export const getSales = async (uuid, query) => {
  return instance.get(`/organizations/sales?uuid=${uuid}&query=${query}`)
}

export const messageAttendees = async (data) => {
  return instance.post(`/organizations/contact-attendees`, data)
}

export const eventOrdersForReport = async (
  uuids,
  types,
  startDate,
  endDate
) => {
  return instance.get(
    `/organizations/report-event-orders?uuids=${uuids}&orderTypes=${types}&start=${startDate}&end=${endDate}`
  )
}

export const createAttractions = async (data) => {
  return instance.post(`/attractions`, data)
}

export const updateAttraction = async (data) => {
  return instance.post('/organizations/update-attraction', data)
}

export const removeAttraction = async (id) => {
  return instance.delete(`/organizations/remove-attraction/${id}`)
}

export const searchAttractions = async (orgUUID, searchTerm) => {
  const encodedOrgUUID = encodeURIComponent(orgUUID ?? '')
  const encodedSearchTerm = encodeURIComponent(searchTerm ?? '')
  return instance.get(
    `/organizations/search-attractions?organizationUUID=${encodedOrgUUID}&searchTerm=${encodedSearchTerm}`
  )
}

export const createorEditTemplate = async (data) => {
  return instance.post(`/organizations/assign-seatmap`, data)
}

export const createOrEditOffers = async (data) => {
  return instance.post(`/organizations/assign-offer`, data)
}

export const createOrEditConnectedOffer = async (data) => {
  return instance.post(`/organizations/create-or-edit-connected-offer`, data)
}

export const scalingInventoryToEvent = async (data) => {
  return instance.post(`/organizations/scaling-inventory-to-event`, data)
}

export const assignSeatLabels = async (data) => {
  return instance.post('/organizations/assign-seat-labels', data)
}

export const createTicketGroups = async (data) => {
  return instance.post('/organizations/create-ticket-groups', data)
}

export const assignSeatmapToEvent = async (data) => {
  return instance.post(`/organizations/set-seatmap`, data)
}

export const applyTemplateToEvent = async (data) => {
  return instance.post(`/organizations/apply-template-to-event`, data)
}

export const resetMapState = async (data) => {
  return instance.post(`/organizations/reset-map-state`, data)
}

export const createSeatmapTemplate = async (data) => {
  return instance.post('/venues/create-template', { data })
}

export const assignAvailabilityPeriod = async (data) => {
  return instance.post(`/organizations/set-availablility`, data)
}

export const getVenueEvents = async (uuid, id, orgUUID) => {
  const params = new URLSearchParams({
    roomUUID: uuid,
    seatmapId: id,
  })
  if (orgUUID) {
    params.set('orgUUID', orgUUID)
  }
  return instance.get(`/packages/get-venue-events?${params.toString()}`)
}

export const createPackage = async (data) => {
  return instance.post('/packages/create', data)
}

export const removePackage = async (id) => {
  return instance.delete(`/packages/remove-package/${id}`)
}

export const getPackages = async (data, signal) => {
  return instance.post(`/packages/get-packages`, data, { signal })
}

export const getPackage = async (uuid) => {
  return instance.get(`/packages/get-package?uuid=${uuid}`)
}

export const getPackageMap = async (uuid, options = {}) => {
  const includeSeatIdentity = options.includeSeatIdentity !== false
  return instance.get(
    `/packages/get-package-map?uuid=${uuid}&includeSeatIdentity=${includeSeatIdentity}`
  )
}

export const getPackageSeatIdentities = async (uuid) => {
  return instance.get(`/packages/get-package-seat-identities?uuid=${uuid}`)
}

export const getPackageStats = async (uuid) => {
  return instance.get(`/packages/package-stats?uuid=${uuid}`)
}

export const validateSeasonPackageInventory = async (data) => {
  return instance.post('/packages/validate-season-inventory', data)
}

export const updatePackageDetails = async (data) => {
  return instance.put(`/packages/update-details`, data)
}

export const updateSeatsInPackage = async (data) => {
  return instance.put(`/packages/update-seats-in-package`, data)
}

export const getInvoiceTickets = async (data) => {
  return instance.post('/invoices/get-invoice-tickets', data)
}

export const publishPackage = async (data) => {
  return instance.post('/packages/publish', data)
}

export const updatePackageStatus = async (data) => {
  return instance.put('/packages/update-package-status', data)
}

export const updatePackageVisibility = async (data) => {
  return instance.put('/packages/update-package-visibility', data)
}

export const updatePackageBlockDirectUrl = async (data) => {
  return instance.put('/packages/update-package-block-direct-url', data)
}

export const getEventStatSheet = async (uuid) => {
  return instance.get(`/organizations/event-stat-sheet?uuid=${uuid}`)
}

export const printTickets = async (data) => {
  return instance.post('/organizations/print-tickets', data)
}

export const getPrinterSettings = async (organizationUUID) => {
  return instance.get(
    `/printer-settings?filters[organizationUUID]=${organizationUUID}&populate=*`
  )
}

export const guestLookup = async (data) => {
  return instance.post(`/orders/guest-lookup`, data)
}

export const specificGuestLookup = async ({ orderId, organizationUUID }) => {
  return instance.get(
    `/orders/specific-guest-lookup?orderId=${orderId}&organizationUUID=${organizationUUID}`
  )
}

export const getListingDetails = async ({ id, organizationUUID }) => {
  return instance.get(
    `/listings/admin-details?id=${encodeURIComponent(id)}&organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const seatLookupFindEventsPackages = async (
  organizationUUID,
  signal
) => {
  return instance.get(
    `/orders/seat-lookup-find-events-packages?organizationUUID=${organizationUUID}`,
    { signal }
  )
}

function buyerWithoutEmail(email, userId) {
  return !String(email || '').trim() && userId
}

export const getUserEvents = async ({ email, organizationUUID, userId }) => {
  if (buyerWithoutEmail(email, userId)) {
    const params = new URLSearchParams()
    if (organizationUUID) params.set('organizationUUID', organizationUUID)
    params.set('userId', String(userId))
    return instance.get(`/orders/get-user-events?${params.toString()}`)
  }
  return instance.get(
    `/orders/get-user-events?email=${email}&organizationUUID=${organizationUUID}`
  )
}

export const getEventTickets = async ({
  email,
  organizationUUID,
  eventUuid,
  userId,
}) => {
  if (buyerWithoutEmail(email, userId)) {
    const params = new URLSearchParams()
    if (organizationUUID) params.set('organizationUUID', organizationUUID)
    if (eventUuid) params.set('eventUuid', eventUuid)
    params.set('userId', String(userId))
    return instance.get(`/orders/get-event-tickets?${params.toString()}`)
  }
  return instance.get(
    `/orders/get-event-tickets?email=${email}&organizationUUID=${organizationUUID}&eventUuid=${eventUuid}`
  )
}

export const adminTransferTickets = async (data) => {
  return instance.post('/ticket-transfers/admin', data)
}

export const getOutgoingTicketTransfers = async ({
  email,
  organizationUUID,
  userId,
}) => {
  const params = new URLSearchParams()
  if (organizationUUID) params.set('organizationUUID', organizationUUID)
  if (email) params.set('email', email)
  if (userId) params.set('userId', String(userId))
  return instance.get(`/ticket-transfers/outgoing?${params.toString()}`)
}

export const getProfileByEmail = async (email) => {
  return instance.get(
    `/profiles/find/by-email?email=${encodeURIComponent(email)}`
  )
}

export const getProfileByUserId = async (userId) => {
  return instance.get(
    `/profiles/find/by-email?userId=${encodeURIComponent(userId)}`
  )
}

export const updateProfileByEmail = async (data) => {
  return instance.post('/profiles/update/by-email', data)
}

export const editOrderAssociatedTo = async (data) => {
  return instance.post(`/orders/edit-order-associated-to`, data)
}

export const updateOrderBuyerType = async (data) => {
  return instance.post(`/orders/update-order-buyer-type`, data)
}

export const sendOrderToUser = async (data) => {
  return instance.post(`/orders/send-order-to-user`, data)
}

export const bulkSendPackageOrders = async (data) => {
  return instance.post(`/orders/bulk-send-package-orders`, data)
}

export const refundReleaseOrder = async (data) => {
  return instance.post(`/orders/refund-release-order`, data)
}

export const voidFlexPackOrder = async (data) => {
  return instance.post(`/orders/void-flex-pack-order`, data)
}

export const undoReleaseOrder = async (data) => {
  return instance.post(`/orders/undo-release-order`, data)
}

export const createOrderAssignment = async (data) => {
  return postWithRetry('/orders/create-order-assignment', data)
}

export const holdAssignmentSeats = async ({ eventUUID, selectedSeats }) => {
  return instance.post('/orders/hold-assignment-seats', {
    eventUUID,
    selectedSeats,
  })
}

export const refreshAssignmentSeatHold = async (holdId) => {
  return instance.post('/orders/refresh-assignment-seat-hold', { holdId })
}

export const releaseAssignmentSeatHold = async (holdId) => {
  return instance.post('/orders/release-assignment-seat-hold', { holdId })
}

export const cancelPendingCheckoutOrder = async (orderId, reason) => {
  return instance.post('/orders/cancel-pending-checkout', { orderId, reason })
}

export const bulkCreateEventReserved = async ({
  eventUUID,
  rows,
  sendConfirmationEmail,
}) => {
  return instance.post('/orders/bulk-reserved-import/event', {
    eventUUID,
    rows,
    sendConfirmationEmail,
  })
}

export const bulkCreateSeasonPackageReserved = async ({
  packageUUID,
  rows,
  sendConfirmationEmail,
}) => {
  return instance.post('/orders/bulk-reserved-import/package', {
    packageUUID,
    rows,
    sendConfirmationEmail,
  })
}

export const getBulkReservedImportStatus = async (batchId) => {
  return instance.get(
    `/orders/bulk-reserved-import/status/${encodeURIComponent(batchId)}`
  )
}

export const createSeasonPackageOrder = async (data) => {
  return postWithRetry('/orders/create-season-package-order', data)
}

export const getOrderByPaymentIntentId = async (paymentIntentId) => {
  const id = String(paymentIntentId || '').trim()
  if (!id) return null
  try {
    const res = await instance.get('/orders/get-order-by-payment-intent-id', {
      params: { paymentIntentId: id },
    })
    return res?.data || null
  } catch (error) {
    if (Number(error?.response?.status) === 404) return null
    // Missing order often comes back as empty/null rather than 404.
    if (!error?.response) throw error
    return null
  }
}

/**
 * After payment succeeds, worker draft recovery may create the order while the
 * admin request loses the inventory race. Poll briefly before surfacing failure
 * so operators do not retry and double-charge.
 */
export const waitForOrderByPaymentIntentId = async (
  paymentIntentId,
  { attempts = 8, baseDelayMs = 400 } = {}
) => {
  const id = String(paymentIntentId || '').trim()
  if (!id) return null

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const order = await getOrderByPaymentIntentId(id)
      if (order?.orderId || order?.id) return order
    } catch (_) {}
    if (attempt < attempts) {
      await sleep(baseDelayMs * attempt)
    }
  }
  return null
}

export const createOrderPaymentPlan = async (data) => {
  return postWithRetry('/orders/create-payment-plan-assignment', data)
}

export const createFlexPackAssignment = async (data) => {
  return postWithRetry('/orders/create-flex-pack-assignment', data)
}

export const createAccessPassAssignment = async (data) => {
  return postWithRetry('/orders/create-access-pass-assignment', data)
}

export const unifiedSearch = async (data, signal) => {
  return instance.post(`/orders/unified-search`, data, { signal })
}

export const searchOrganizationUsers = async (
  organizationUUID,
  query,
  signal
) => {
  return instance.get('/orders/organization-users', {
    params: { organizationUUID, q: query },
    signal,
  })
}

export const getOrganizationUserOrders = async (
  organizationUUID,
  userId,
  signal
) => {
  return instance.get('/orders/organization-user-orders', {
    params: { organizationUUID, userId },
    signal,
  })
}

export const sendInvoice = async (data) => {
  return instance.post(`/stripe-invoice/send-invoice`, data)
}

export const listReservedPackageInvoiceHolders = async (data) => {
  return instance.post(`/invoices/reserved-package-invoice-holders`, data)
}

export const bulkSendReservedPackageInvoices = async (data) => {
  return instance.post(`/invoices/bulk-send-reserved-package-invoices`, data)
}

export const getBulkReservedInvoiceSendStatus = async (batchId) => {
  return instance.get(
    `/invoices/bulk-send-reserved-package-invoices/status/${encodeURIComponent(batchId)}`
  )
}

export const getOrgEventsForReport = async (organizationUUID) => {
  return instance.get(`/audit/org-events?organizationUUID=${organizationUUID}`)
}

export const getTicketReportByEvent = async (eventUUID, organizationUUID) => {
  return instance.get(
    `/audit/ticket-report-by-event?eventUUID=${eventUUID}&organizationUUID=${organizationUUID}`
  )
}

export const seatLookupReleaseOrderTickets = async (data) => {
  return instance.post(`/orders/seat-lookup-release-order-tickets`, data)
}

export const getROEventSalesReport = async (eventUUID) => {
  return instance.post(
    `/analytic/event-sales-report?eventUUID=${eventUUID}`,
    {}
  )
}

export const ticketExchangeSearch = async (data) => {
  return instance.post(`/ticket-exchange/search`, data)
}

export const ticketExchangeSeatMap = async (data) => {
  return instance.post(`/ticket-exchange/seatmap`, data)
}

export const exchangeTickets = async (data) => {
  return instance.post(`/ticket-exchange/exchange`, data)
}

export const ticketExchangeGetEvents = async (uuid) => {
  return instance.get(`/ticket-exchange/events?organizationUUID=${uuid}`)
}

export const ticketExchangeGetTickets = async (uuid) => {
  return instance.get(`/ticket-exchange/tickets?organizationUUID=${uuid}`)
}

export const createNewSeason = async (data) => {
  return instance.post(`/seasons/create-new-season`, data)
}

export const getSeasons = async (organizationUUID) => {
  if (organizationUUID === '9bd92013-a296-4f46-97cb-e0d248c6c3ca') {
    return instance.get('/seasons/getSeasons')
  } else {
    return instance.get(
      `/seasons/getSeasons?organizationUUID=${organizationUUID}`
    )
  }
}

export const getHolders = async (data, signal) => {
  return instance.post(`/seasons/holders`, data, { signal })
}

export const getFlexPackEvents = async (organizationUUID) => {
  return instance.get(`/flex-pack/events?organizationUUID=${organizationUUID}`)
}

export const createOrEditFlexPack = async (data) => {
  return instance.post(`/flex-pack/create`, data)
}

export const getGroupPurchases = async (organizationUUID, page, pageSize) => {
  return instance.get(
    `/seasons/group-purchases?organizationUUID=${organizationUUID}&page=${page}&pageSize=${pageSize}`
  )
}

export const getSeasonSummary = async (organizationUUID, seasonId) => {
  return instance.get(
    `/analytic/season-summary?organizationUUID=${organizationUUID}&seasonId=${seasonId}`
  )
}

export const getDeferredRevenueReport = async (
  organizationUUID,
  seasonId,
  asOfDate,
  periodStart,
  includeAdvanceTickets = true
) => {
  const params = new URLSearchParams({
    organizationUUID,
    asOfDate,
    includeAdvanceTickets: String(includeAdvanceTickets),
  })
  if (seasonId != null && seasonId !== '') {
    params.set('seasonId', String(seasonId))
  }
  if (periodStart) {
    params.set('periodStart', periodStart)
  }
  return instance.get(`/analytic/deferred-revenue?${params.toString()}`)
}

export const getFlexPackDeferredRevenueReport = async (
  organizationUUID,
  seasonId,
  asOfDate,
  periodStart
) => {
  const params = new URLSearchParams({
    organizationUUID,
    asOfDate,
  })
  if (seasonId != null && seasonId !== '') {
    params.set('seasonId', String(seasonId))
  }
  if (periodStart) {
    params.set('periodStart', periodStart)
  }
  return instance.get(
    `/analytic/flex-pack-deferred-revenue?${params.toString()}`
  )
}

export const getDailyTicketCounts = async (organizationUUID) => {
  return instance.get(
    `/analytic/daily-ticket-counts?organizationUUID=${organizationUUID}`
  )
}

export const getEventsForSettlement = async (organizationUUID) => {
  return instance.get(
    `/analytic/events-for-settlement?organizationUUID=${organizationUUID}`
  )
}

export const getSettlement = async (eventUUID) => {
  return instance.get(`/analytic/settlement-report?eventUUID=${eventUUID}`)
}

export const getFlexPacks = async (data, signal) => {
  return instance.post(`/flex-pack/get-all`, data, { signal })
}

export const getAttendanceSummary = async (eventUUID) => {
  return instance.get(`/analytic/attendance-summary?eventUUID=${eventUUID}`)
}

export const getOrganizationDashboardDaily = async (
  organizationUUID,
  from,
  to,
  options = {}
) => {
  const { includeAiPricing = true } = options
  let url = `/analytic/organization-dashboard-daily?organizationUUID=${encodeURIComponent(organizationUUID)}`
  if (from) url += `&from=${encodeURIComponent(from)}`
  if (to) url += `&to=${encodeURIComponent(to)}`
  if (!includeAiPricing) url += '&includeAiPricing=false'
  return instance.get(url)
}

export const getSalesVelocityVenueHeatmap = async (
  organizationUUID,
  venueUUID
) => {
  return instance.get(
    `/analytic/sales-velocity-venue-heatmap?organizationUUID=${encodeURIComponent(organizationUUID)}&venueUUID=${encodeURIComponent(venueUUID)}`
  )
}

export const getPricingLevelPerformance = async (
  organizationUUID,
  eventUUID
) => {
  return instance.get(
    `/analytic/pricing-level-performance?organizationUUID=${encodeURIComponent(organizationUUID)}&eventUUID=${encodeURIComponent(eventUUID)}`
  )
}

export const getCartSessionAnalytics = async (
  organizationUUID,
  eventUUID,
  startDate,
  endDate
) => {
  let url = `/cart-sessions/analytics?organizationUUID=${encodeURIComponent(organizationUUID)}`
  if (eventUUID) {
    url += `&eventUUID=${encodeURIComponent(eventUUID)}`
  }
  if (startDate) {
    url += `&startDate=${encodeURIComponent(startDate)}`
  }
  if (endDate) {
    url += `&endDate=${encodeURIComponent(endDate)}`
  }
  return instance.get(url)
}

export const getAttendanceIntelligence = async (
  organizationUUID,
  eventUUID
) => {
  return instance.get(
    `/analytic/attendance-intelligence?organizationUUID=${encodeURIComponent(organizationUUID)}&eventUUID=${encodeURIComponent(eventUUID)}`
  )
}

export const getGeographicSalesIntelligence = async (
  organizationUUID,
  { seasonId, eventUUID } = {}
) => {
  const params = new URLSearchParams({
    organizationUUID: String(organizationUUID),
  })
  if (seasonId) params.set('seasonId', String(seasonId))
  if (eventUUID) params.set('eventUUID', String(eventUUID))
  return instance.get(`/analytic/geographic-sales-intelligence?${params}`)
}

export const getWeatherEventAnalytics = async (organizationUUID, eventUUID) => {
  return instance.get(
    `/analytic/weather-event-analytics?organizationUUID=${encodeURIComponent(organizationUUID)}&eventUUID=${encodeURIComponent(eventUUID)}`
  )
}

export const getFnbAnalytics = async (organizationUUID, eventUUID) => {
  return instance.get(
    `/analytic/fnb-analytics?organizationUUID=${encodeURIComponent(organizationUUID)}&eventUUID=${encodeURIComponent(eventUUID)}`
  )
}

export const getFnbEodReport = async (
  organizationUUID,
  { eventUUID, venueUuid, locationId, reportDate } = {}
) => {
  const params = new URLSearchParams({ organizationUUID })
  if (eventUUID) params.set('eventUUID', eventUUID)
  if (venueUuid) params.set('venueUuid', venueUuid)
  if (locationId) params.set('locationId', locationId)
  if (reportDate) params.set('reportDate', reportDate)
  return instance.get(`/analytic/fnb-eod-report?${params.toString()}`)
}

export const getFnbEodInventory = async (
  organizationUUID,
  { eventUUID, locationId, reportDate } = {}
) => {
  const params = new URLSearchParams({
    organizationUUID,
    locationId,
  })
  if (eventUUID) params.set('eventUUID', eventUUID)
  if (reportDate) params.set('reportDate', reportDate)
  return instance.get(`/analytic/fnb-eod-inventory?${params.toString()}`)
}

export const saveFnbEodInventory = async (
  organizationUUID,
  { eventUUID, locationId, reportDate, entries } = {}
) => {
  return instance.put('/analytic/fnb-eod-inventory', {
    organizationUUID,
    ...(eventUUID ? { eventUUID } : {}),
    locationId,
    reportDate,
    entries,
  })
}

export const getFnbEodCounts = async (
  organizationUUID,
  { eventUUID, locationId, reportDate } = {}
) => {
  const params = new URLSearchParams({
    organizationUUID,
    locationId,
  })
  if (eventUUID) params.set('eventUUID', eventUUID)
  if (reportDate) params.set('reportDate', reportDate)
  return instance.get(`/analytic/fnb-eod-counts?${params.toString()}`)
}

export const saveFnbEodCounts = async (
  organizationUUID,
  { eventUUID, locationId, reportDate, entries, countedByEmployeeId } = {}
) => {
  return instance.put('/analytic/fnb-eod-counts', {
    organizationUUID,
    ...(eventUUID ? { eventUUID } : {}),
    locationId,
    reportDate,
    entries,
    countedByEmployeeId,
  })
}

export const getRevenueInsights = async (
  organizationUUID,
  { eventUUID, status } = {}
) => {
  const params = new URLSearchParams({ organizationUUID })
  if (eventUUID) params.set('eventUUID', eventUUID)
  if (status) params.set('status', status)
  return instance.get(`/revenue-insight/insights?${params.toString()}`)
}

export const getRevenueComparisonPreview = async (
  organizationUUID,
  eventUUID,
  snapshotDate
) => {
  const params = new URLSearchParams({ organizationUUID, eventUUID })
  if (snapshotDate) params.set('snapshotDate', snapshotDate)
  return instance.get(`/revenue-insight/compare-event?${params.toString()}`)
}

export const runRevenueComparison = async (
  organizationUUID,
  eventUUID,
  snapshotDate
) => {
  return instance.post('/revenue-insight/run-comparison', {
    organizationUUID,
    eventUUID,
    snapshotDate: snapshotDate || undefined,
  })
}

export const getRevenueIntelligenceInsights = async (
  organizationUUID,
  { type, status, limit } = {}
) => {
  const params = new URLSearchParams({ organizationUUID })
  if (type) params.set('type', type)
  if (status) params.set('status', status)
  if (limit) params.set('limit', String(limit))
  return instance.get(`/revenue-intelligence/insights?${params.toString()}`)
}

export const getWeekdayUnderperformance = async (organizationUUID) => {
  return instance.get(
    `/revenue-intelligence/weekday-underperformance?organizationUUID=${encodeURIComponent(organizationUUID)}`
  )
}

export const approveRevenueRecommendation = async (
  organizationUUID,
  recommendationId
) => {
  return instance.post(
    `/revenue-intelligence/recommendations/${recommendationId}/approve`,
    { organizationUUID }
  )
}

export const dismissRevenueRecommendation = async (
  organizationUUID,
  recommendationId
) => {
  return instance.post(
    `/revenue-intelligence/recommendations/${recommendationId}/dismiss`,
    { organizationUUID }
  )
}

export const getClientReport = async (from, to) => {
  let url = '/analytic/client-report'
  const query = []
  if (from) query.push(`from=${encodeURIComponent(from)}`)
  if (to) query.push(`to=${encodeURIComponent(to)}`)
  if (query.length) url += `?${query.join('&')}`
  return instance.get(url)
}

export const getAccountingReport = async (organizationUUID, from, to) => {
  const query = [`organizationUUID=${encodeURIComponent(organizationUUID)}`]
  if (from) query.push(`from=${encodeURIComponent(from)}`)
  if (to) query.push(`to=${encodeURIComponent(to)}`)
  return instance.get(`/analytic/accounting-report?${query.join('&')}`)
}

export const getFlexPack = async (uuid) => {
  return instance.get(`/flex-pack/get-flex-pack?uuid=${uuid}`)
}

export const publishFlexPack = async (data) => {
  return instance.post('/flex-pack/publish', data)
}

export const updateFlexPackStatus = async (data) => {
  return instance.put('/flex-pack/update-flex-pack-status', data)
}

export const removeFlexPack = async (id) => {
  return instance.delete(`/flex-pack/remove-flex-pack/${id}`)
}

export const updateFlexPackVisibility = async (data) => {
  return instance.put('/flex-pack/update-flex-pack-visibility', data)
}

export const createAccessPass = async (data) => {
  return instance.post('/organizations/create-access-pass', data)
}

export const updateAccessPass = async (data) => {
  return instance.put('/organizations/update-access-pass', data)
}

export const assignAccessPass = async (data) => {
  return instance.post('/organizations/assign-access-pass', data)
}

export const getAccessPasses = async (organizationUUID, params = {}) => {
  return instance.get('/organizations/access-passes', {
    params: { organizationUUID, ...params },
  })
}

export const getAccessPassEvents = async (organizationUUID) => {
  return instance.get('/organizations/access-pass-events', {
    params: { organizationUUID },
  })
}

export const getAccessPassTemplates = async (organizationUUID) => {
  return instance.get('/organizations/access-pass-templates', {
    params: { organizationUUID },
  })
}

export const createAccessPassTemplate = async (data) => {
  return instance.post('/organizations/create-access-pass-template', data)
}

export const updateAccessPassTemplate = async (data) => {
  return instance.put('/organizations/update-access-pass-template', data)
}

export const deleteAccessPassTemplate = async (data) => {
  return instance.post('/organizations/delete-access-pass-template', data)
}

export const getAllOrganizations = async () => {
  return instance.get('/setting/all-orgs')
}

export const getOrganizationSettings = async (organizationUUID) => {
  return instance.get(
    `/setting/organization-settings?organizationUUID=${organizationUUID}`
  )
}

export const updateOrganizationSettings = async (
  organizationUUID,
  settings
) => {
  return instance.put('/setting/organization-settings', {
    organizationUUID,
    settings,
  })
}

export const getOrganizationFeeConfig = async (organizationUUID) => {
  return instance.get(
    `/setting/organization-fee-config?organizationUUID=${organizationUUID}`
  )
}

export const updateOrganizationFeeConfig = async (organizationUUID, rules) => {
  return instance.put('/setting/organization-fee-config', {
    organizationUUID,
    rules,
  })
}

export const getReaders = async (organizationUUID) => {
  return instance.get(`/setting/readers?organizationUUID=${organizationUUID}`)
}

export const getTeamSettings = async (organizationUUID) => {
  return instance.get(`/setting/get-team?organizationUUID=${organizationUUID}`)
}

export const getLocations = async (organizationUUID) => {
  return instance.get(`/setting/locations?organizationUUID=${organizationUUID}`)
}

export const postLocation = async (data) => {
  return instance.post(`/setting/create-edit-location`, data)
}

export const assignReader = async (data) => {
  return instance.post('/setting/assign-reader', data)
}

export const createOrEditReader = async (data) => {
  return instance.post('/setting/create-edit-reader', data)
}

export const getEntryPoints = async (organizationUUID) => {
  return instance.get(
    `/setting/entry-points?organizationUUID=${organizationUUID}`
  )
}

export const createOrEditEntryPoint = async (data) => {
  return instance.post('/setting/create-edit-entry-point', data)
}

export const getScanners = async (organizationUUID) => {
  return instance.get(`/setting/scanners?organizationUUID=${organizationUUID}`)
}

export const createOrEditScanner = async (data) => {
  return instance.post('/setting/create-edit-scanner', data)
}

export const getAccountBalance = async (accountId) => {
  return instance.get(
    `/organizations/get-account-balance?accountId=${accountId}`
  )
}

export const getAccountSession = async (data) => {
  return instance.post('/organizations/account-session', data)
}

const BUYER_TYPES_LIST_PAGE_SIZE = 100

export const getBuyerTypes = async (organizationUUID) => {
  return instance.get(
    `/buyer-types?filters[organizationUUID][$eq]=${organizationUUID}&filters[active][$eq]=true&sort[0]=name:asc&pagination[pageSize]=${BUYER_TYPES_LIST_PAGE_SIZE}`
  )
}

export const getBuyerTypesForOrganization = async (organizationUUID) => {
  return instance.get(
    `/buyer-types?filters[organizationUUID][$eq]=${organizationUUID}&filters[active][$eq]=true&sort[0]=name:asc&pagination[pageSize]=${BUYER_TYPES_LIST_PAGE_SIZE}`
  )
}

/** Active buyer types for an org (settings UI); deactivated stay in DB with active: false */
export const getBuyerTypesForSettings = async (organizationUUID) => {
  return instance.get(
    `/buyer-types?filters[organizationUUID][$eq]=${organizationUUID}&filters[active][$eq]=true&sort[0]=name:asc&pagination[pageSize]=${BUYER_TYPES_LIST_PAGE_SIZE}`
  )
}

/** System-seeded defaults (systemDefault true); not filtered by organization */
export const getSystemDefaultBuyerTypes = async () => {
  return instance.get(
    `/buyer-types?filters[systemDefault][$eq]=true&filters[active][$eq]=true&sort[0]=name:asc&pagination[pageSize]=${BUYER_TYPES_LIST_PAGE_SIZE}`
  )
}

export const createBuyerType = async (payload) => {
  return instance.post('/buyer-types', { data: payload })
}

export const updateBuyerType = async (id, payload) => {
  return instance.put(`/buyer-types/${id}`, { data: payload })
}

export const deleteBuyerType = async (id) => {
  return instance.delete(`/buyer-types/${id}`)
}

export const getCustomFees = async (organizationUUID) => {
  return instance.get(
    `/custom-fees?filters[organizationUUID][$eq]=${organizationUUID}&sort[0]=name:asc`
  )
}

export const createCustomFee = async (payload) => {
  return instance.post('/custom-fees', { data: payload })
}

export const updateCustomFee = async (id, payload) => {
  return instance.put(`/custom-fees/${id}`, { data: payload })
}

export const deleteCustomFee = async (id) => {
  return instance.delete(`/custom-fees/${id}`)
}

export const getFnbItems = async (organizationId) => {
  return instance.get(
    `/fnb-items?filters[organizationId][$eq]=${organizationId}&sort[0]=name:asc&pagination[pageSize]=500&populate=imageUrl`
  )
}

export const getFnbItem = async (id) => {
  return instance.get(`/fnb-items/${id}?populate=imageUrl`)
}

export const createFnbItem = async (payload) => {
  return instance.post('/fnb-items', { data: payload })
}

export const updateFnbItem = async (id, payload) => {
  return instance.put(`/fnb-items/${id}`, { data: payload })
}

export const deleteFnbItem = async (id) => {
  return instance.delete(`/fnb-items/${id}`)
}

export const getFnbCategories = async (organizationUUID) => {
  return instance.get(
    `/fnb-categories?filters[organization_uuid][$eq]=${organizationUUID}&sort[0]=displayOrder:asc&sort[1]=name:asc&pagination[pageSize]=200`
  )
}

export const createFnbCategory = async (payload) => {
  return instance.post('/fnb-categories', { data: payload })
}

export const updateFnbCategory = async (id, payload) => {
  return instance.put(`/fnb-categories/${id}`, { data: payload })
}

export const deleteFnbCategory = async (id) => {
  return instance.delete(`/fnb-categories/${id}`)
}

export const getFnbLocations = async (organizationUUID) => {
  return instance.get(
    `/fnb-locations?filters[organization_uuid][$eq]=${organizationUUID}&sort[0]=sortOrder:asc&sort[1]=name:asc&pagination[pageSize]=200`
  )
}

export const createFnbLocation = async (payload) => {
  return instance.post('/fnb-locations', { data: payload })
}

export const updateFnbLocation = async (id, payload) => {
  return instance.put(`/fnb-locations/${id}`, { data: payload })
}

export const deleteFnbLocation = async (id) => {
  return instance.delete(`/fnb-locations/${id}`)
}

export const getFnbFulfillmentQueue = async (params) => {
  return instance.get('/fnb-orders/fulfillment-queue', { params })
}

export const transitionFnbOrder = async (orderId, payload) => {
  return instance.post(`/fnb-orders/${orderId}/fulfillment-transition`, {
    data: payload,
  })
}

export const collectFnbOrderPayment = async (orderId, payload) => {
  return instance.post(`/fnb-orders/${orderId}/collect-payment`, {
    data: payload,
  })
}

export const getDonationRules = async (organizationUUID) => {
  return instance.get(
    `/donation-rules/for-organization?organizationUUID=${organizationUUID}`
  )
}

export const createDonationRule = async (payload) => {
  return instance.post('/donation-rules', { data: payload })
}

export const updateDonationRule = async (id, payload) => {
  return instance.put(`/donation-rules/${id}`, { data: payload })
}

export const evaluateDonationAssignment = async (payload) => {
  return instance.post('/donation-rules/evaluate-assignment', payload)
}

export const getFanDonationRequirements = async (
  organizationUUID,
  fanEmail
) => {
  return instance.get(
    `/donation-requirements/for-fan?organizationUUID=${organizationUUID}&fanEmail=${encodeURIComponent(fanEmail)}`
  )
}

export const recordDonationRequirement = async (payload) => {
  return instance.post('/donation-requirements/record', payload)
}

export const chargeDonationInvoice = async (payload) => {
  return instance.post('/donation-requirements/charge-invoice', payload)
}

export const markDonationRequirementSatisfied = async (payload) => {
  return instance.post('/donation-requirements/mark-satisfied', payload)
}

export const getYoyDifferenceReport = async (organizationUUID, seasonId) => {
  const params = new URLSearchParams({ organizationUUID })
  if (seasonId) {
    params.set('seasonId', seasonId)
  }
  return instance.get(`/orders/yoy-difference-report?${params.toString()}`)
}

export const getYoyDifferenceConfig = async (organizationUUID) => {
  return instance.get(
    `/yoy-difference-config?organizationUUID=${organizationUUID}`
  )
}

export const saveYoyDifferenceConfig = async (payload) => {
  return instance.post('/yoy-difference-config', { data: payload })
}

export const getYoyDifferenceEmailSubscription = async (organizationUUID) => {
  return instance.get(
    `/yoy-difference-email-subscription?organizationUUID=${organizationUUID}`
  )
}

export const saveYoyDifferenceEmailSubscription = async (payload) => {
  return instance.post('/yoy-difference-email-subscription', { data: payload })
}

export const deleteYoyDifferenceEmailSubscription = async (
  organizationUUID
) => {
  return instance.delete(
    `/yoy-difference-email-subscription?organizationUUID=${organizationUUID}`
  )
}

export const sendYoyDifferenceTestEmail = async (payload) => {
  return instance.post('/yoy-difference-email-subscription/send-test', {
    data: payload,
  })
}

export const getDeferredRevenueEmailSubscription = async (organizationUUID) => {
  return instance.get(
    `/deferred-revenue-email-subscription?organizationUUID=${organizationUUID}`
  )
}

export const saveDeferredRevenueEmailSubscription = async (payload) => {
  return instance.post('/deferred-revenue-email-subscription', {
    data: payload,
  })
}

export const deleteDeferredRevenueEmailSubscription = async (
  organizationUUID
) => {
  return instance.delete(
    `/deferred-revenue-email-subscription?organizationUUID=${organizationUUID}`
  )
}

export const sendDeferredRevenueTestEmail = async (payload) => {
  return instance.post('/deferred-revenue-email-subscription/send-test', {
    data: payload,
  })
}

export const getFlexPackDeferredRevenueEmailSubscription = async (
  organizationUUID
) => {
  return instance.get(
    `/flex-pack-deferred-revenue-email-subscription?organizationUUID=${organizationUUID}`
  )
}

export const saveFlexPackDeferredRevenueEmailSubscription = async (payload) => {
  return instance.post('/flex-pack-deferred-revenue-email-subscription', {
    data: payload,
  })
}

export const deleteFlexPackDeferredRevenueEmailSubscription = async (
  organizationUUID
) => {
  return instance.delete(
    `/flex-pack-deferred-revenue-email-subscription?organizationUUID=${organizationUUID}`
  )
}

export const sendFlexPackDeferredRevenueTestEmail = async (payload) => {
  return instance.post(
    '/flex-pack-deferred-revenue-email-subscription/send-test',
    {
      data: payload,
    }
  )
}

export const getCompLabels = async (organizationUUID) => {
  return instance.get(
    `/comp-labels?filters[organizationUUID][$eq]=${organizationUUID}&sort[0]=name:asc`
  )
}

export const getPaymentIntent = async (data) => {
  return instance.post(`/payment/intent`, data)
}

export const createAdminDeposit = async (data) => {
  return instance.post(`/deposits/admin-create`, data)
}

export const getDepositsForOrganization = async (organizationId) => {
  const id = encodeURIComponent(organizationId ?? '')
  return instance.get(`/deposits/for-organization?organizationId=${id}`)
}

export const getPayoutTransactions = async (accountId, payoutId) => {
  return instance.get(
    `/organizations/get-payout-transactions?accountId=${accountId}&payoutId=${payoutId}`
  )
}

export const getOrganizationPaymentPlans = async (organizationUUID) => {
  return instance.get(
    `/organizations/get-organization-payment-plans?organizationUUID=${organizationUUID}`
  )
}

export const chargePaymentPlanNextInstallment = async (planId, data = {}) => {
  return instance.post(
    `/payment-plan/${encodeURIComponent(planId)}/charge-next-installment`,
    data
  )
}

export const payPaymentPlanRemainingBalance = async (planId, data = {}) => {
  return instance.post(
    `/payment-plan/${encodeURIComponent(planId)}/pay-remaining-balance`,
    data
  )
}

export const getAgentSales = async (orgUUID) => {
  return instance.get(
    `/analytic/agent-sales-summary?organizationUUID=${orgUUID}`
  )
}

export const getPromoCodes = async (orgUUID) => {
  return instance.get(
    `/promo-code/get-promo-codes?organizationUUUID=${orgUUID}`
  )
}

export const createEditPromoCode = async (data) => {
  return instance.post(`/promo-code/create-or-edit`, data)
}

export const updatePromoCodeActive = async (data) => {
  return instance.put('/promo-code/update-promo-code-active', data)
}

export const deletePromoCode = async (id) => {
  return instance.delete(`/promo-codes/${id}`)
}

export const pastPurchasers = async (orgUUID, searchTerm) => {
  const encodedOrgUUID = encodeURIComponent(orgUUID ?? '')
  const encodedSearchTerm = encodeURIComponent(searchTerm ?? '')
  return instance.get(
    `/organizations/past-purchasers?organizationUUID=${encodedOrgUUID}&searchTerm=${encodedSearchTerm}`
  )
}

export const customerPaymentMethods = async (connectAccountId, email) => {
  return instance.get(
    `/organizations/customer-payment-methods?connectAccountId=${connectAccountId}&email=${email}`
  )
}

export const customerPaymentMethodsByCustomerId = async (
  stripeCustomerId,
  connectAccountId
) => {
  const encodedCustomerId = encodeURIComponent(stripeCustomerId ?? '')
  const encodedAccountId = encodeURIComponent(connectAccountId ?? '')
  return instance.get(
    `/organizations/customer-payment-methods-by-customer-id?stripeCustomerId=${encodedCustomerId}&connectAccountId=${encodedAccountId}`
  )
}

export const createSetupIntent = async (data) => {
  return instance.post('/organizations/create-setup-intent', data)
}

export const setDefaultPaymentMethod = async (data) => {
  return instance.post('/organizations/set-default-payment-method', data)
}

export const userOrderHistory = async ({ email, organizationUUID, userId }) => {
  if (buyerWithoutEmail(email, userId)) {
    const params = new URLSearchParams()
    if (organizationUUID) params.set('organizationUUID', organizationUUID)
    params.set('userId', String(userId))
    return instance.get(
      `/organizations/user-order-history?${params.toString()}`
    )
  }
  return instance.get(
    `/organizations/user-order-history?organizationUUID=${organizationUUID}&email=${email}`
  )
}

export const getTicketAssignmentPaymentIntent = async (data) => {
  return instance.post('/payment/ticket-assignment-intent', data)
}

export const getConnectionToken = async () => {
  return instance.get('/organizations/connection-token')
}

export const getTicketScanReport = async (eventUUID) => {
  return instance.get(`/analytic/ticket-scans-report?eventUUID=${eventUUID}`)
}

export const getTicketInventoryMatrix = async (organizationUUID) => {
  return instance.get(
    `/audit/tickets-inventory-matrix?organizationUUID=${organizationUUID}`
  )
}

export const updatePaymentIntent = async (data) => {
  return instance.put(`/payment/update-payment-intent`, data)
}

export const changeExistingPaymentMethod = async (data) => {
  return instance.post('/payment/charge-existing-payment-method', data)
}

export const cancelTerminalPaymentIntent = async (data) => {
  return instance.post('/payment/cancel-terminal-payment-intent', data)
}

export const renewSeasonPackageOrder = async (data) => {
  return instance.post('/orders/renew-season-package-order', data)
}

export const releaseSeasonPackageOrder = async (data) => {
  return instance.post('/orders/release-season-package-order', data)
}

export const removeSeatFromRenewalOrder = async (
  orderId,
  section,
  row,
  seat
) => {
  return instance.post('/orders/remove-seat-from-renewal-order', {
    orderId,
    section,
    row,
    seat,
  })
}

export const removeSeatsFromPackageOrder = async (data) => {
  return instance.post('/orders/remove-seats-from-package-order', data)
}

export const removeTicketsFromEventOrder = async (data) => {
  return instance.post('/orders/remove-tickets-from-event-order', data)
}

export const relocateSeasonPackageOrderSeats = async (data) => {
  return instance.post('/orders/relocate-season-package-order-seats', data)
}

export const addSeasonPackageOrderSeats = async (data) => {
  return instance.post('/orders/add-season-package-order-seats', data)
}

export const createSupplementalCustomFeeOrder = async (data) => {
  return instance.post('/orders/create-supplemental-custom-fee-order', data)
}

export const getPaymentListingsV4 = async (
  organizationUUID,
  from,
  to,
  eventUuid,
  statuses,
  source,
  paymentType,
  productType,
  buyerType,
  boxOfficeAgent,
  options = {},
  seasonId
) => {
  const { dateField, datePaidOn, datePaidFrom, datePaidTo } = options
  let url = `/orders/payment-listings-v4?organizationUUID=${organizationUUID}&from=${from}&to=${to}`
  if (eventUuid) {
    url += `&eventUuid=${eventUuid}`
  }
  if (statuses && statuses.length > 0) {
    url += `&statuses=${statuses.join(',')}`
  }
  if (source) {
    url += `&source=${source}`
  }
  if (paymentType) {
    url += `&paymentType=${paymentType}`
  }
  if (productType) {
    url += `&productType=${productType}`
  }
  if (buyerType && buyerType !== 'all') {
    url += `&buyerType=${encodeURIComponent(buyerType)}`
  }
  if (boxOfficeAgent) {
    url += `&boxOfficeAgent=${encodeURIComponent(boxOfficeAgent)}`
  }
  if (dateField) {
    url += `&dateField=${encodeURIComponent(dateField)}`
  }
  if (datePaidOn) {
    url += `&datePaidOn=${encodeURIComponent(datePaidOn)}`
  }
  if (datePaidFrom && datePaidTo) {
    url += `&datePaidFrom=${encodeURIComponent(datePaidFrom)}&datePaidTo=${encodeURIComponent(datePaidTo)}`
  }
  if (seasonId) {
    url += `&seasonId=${encodeURIComponent(seasonId)}`
  }
  return instance.get(url)
}

export const getCustomFeeReport = async (
  organizationUUID,
  from,
  to,
  eventUuid,
  statuses,
  source,
  paymentType,
  productType,
  buyerType,
  boxOfficeAgent,
  options = {}
) => {
  const { dateField, datePaidOn, datePaidFrom, datePaidTo } = options
  let url = `/orders/custom-fee-report?organizationUUID=${organizationUUID}&from=${from}&to=${to}`
  if (eventUuid) {
    url += `&eventUuid=${eventUuid}`
  }
  if (statuses && statuses.length > 0) {
    url += `&statuses=${statuses.join(',')}`
  }
  if (source) {
    url += `&source=${source}`
  }
  if (paymentType) {
    url += `&paymentType=${paymentType}`
  }
  if (productType) {
    url += `&productType=${productType}`
  }
  if (buyerType && buyerType !== 'all') {
    url += `&buyerType=${encodeURIComponent(buyerType)}`
  }
  if (boxOfficeAgent) {
    url += `&boxOfficeAgent=${encodeURIComponent(boxOfficeAgent)}`
  }
  if (dateField) {
    url += `&dateField=${encodeURIComponent(dateField)}`
  }
  if (datePaidOn) {
    url += `&datePaidOn=${encodeURIComponent(datePaidOn)}`
  }
  if (datePaidFrom && datePaidTo) {
    url += `&datePaidFrom=${encodeURIComponent(datePaidFrom)}&datePaidTo=${encodeURIComponent(datePaidTo)}`
  }
  return instance.get(url)
}

export const getHensonFundReport = async (
  organizationUUID,
  from,
  to,
  statuses,
  options = {}
) => {
  const { dateField, datePaidFrom, datePaidTo, email } = options
  let url = `/orders/henson-fund-report?organizationUUID=${organizationUUID}`
  if (from && to) {
    url += `&from=${from}&to=${to}`
  }
  if (statuses && statuses.length > 0) {
    url += `&statuses=${statuses.join(',')}`
  }
  if (dateField) {
    url += `&dateField=${encodeURIComponent(dateField)}`
  }
  if (datePaidFrom && datePaidTo) {
    url += `&datePaidFrom=${encodeURIComponent(datePaidFrom)}&datePaidTo=${encodeURIComponent(datePaidTo)}`
  }
  if (email) {
    url += `&email=${encodeURIComponent(email)}`
  }
  return instance.get(url)
}

export const markAsPaid = async (data) => {
  return instance.post(`/orders/mark-as-paid`, data)
}

export const detachPaymentMethod = async (data) => {
  return instance.post(`/organizations/detach-payment-method`, data)
}

// ── Dynamic Pricing ─────────────────────────────────────────────────────────

export const getDynamicPricingRules = async (eventUUID) => {
  return instance.get(`/dynamic-pricing-rules/event/${eventUUID}`)
}

export const upsertDynamicPricingRule = async (data) => {
  return instance.post(`/dynamic-pricing-rules/upsert`, { data })
}

export const deleteDynamicPricingRule = async (id) => {
  return instance.delete(`/dynamic-pricing-rules/${id}`)
}

export const applyDynamicPricingRules = async (eventUUID) => {
  return instance.post(`/dynamic-pricing-rules/apply/${eventUUID}`)
}

export const previewDynamicPricingRules = async (
  eventUUID,
  organizationUUID
) => {
  return instance.get(
    `/dynamic-pricing-rules/preview/${eventUUID}?organizationUUID=${organizationUUID}`
  )
}

export const updateEventDynamicPricing = async (eventId, enabled, strategy) => {
  return instance.put(`/events/update-event`, {
    data: {
      id: eventId,
      dynamicPricingEnabled: enabled,
      dynamicPricingStrategy: strategy ?? 'stack',
    },
  })
}

export const getDynamicPricingSettlement = async (
  organizationUUID,
  eventUUID
) => {
  return instance.get(
    `/analytic/dynamic-pricing-settlement?organizationUUID=${organizationUUID}&eventUUID=${eventUUID}`
  )
}

export const postAiReportPlan = async (body) => {
  return instance.post('/report/ai-report-plan', body)
}

export const postDraftEventsAssistantMessage = async (body, file = null) => {
  if (!file) {
    return instance.post('/organizations/draft-events-assistant/message', body)
  }

  const formData = new FormData()
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    formData.append(
      key,
      typeof value === 'string' ? value : JSON.stringify(value)
    )
  })
  formData.append('file', file)
  return instance.post(
    '/organizations/draft-events-assistant/message',
    formData
  )
}

export const createDraftEventsFromAssistant = async (body) => {
  return instance.post('/organizations/draft-events-assistant/create', body)
}

export const getDraftEventsAssistantProgress = async (organizationUUID) => {
  return instance.get('/organizations/draft-events-assistant/progress', {
    params: { organizationUUID },
  })
}

export const generateDraftEventsAssistantSpeech = async (body) => {
  const response = await instance.post(
    '/organizations/draft-events-assistant/speech',
    body,
    { responseType: 'blob', timeout: 60000 }
  )
  return response.data
}

export const exportDraftEventsAssistantReport = async (body) => {
  return instance.post(
    '/organizations/draft-events-assistant/report-export',
    body
  )
}

export const getNewFeatures = async () => {
  return instance.get('/new-features')
}

export const getNewFeatureMedia = async () => {
  return instance.get('/new-features/media')
}

export const createNewFeature = async (body) => {
  return instance.post('/new-features/feature', body)
}

export const updateNewFeature = async (body) => {
  return instance.put('/new-features/feature', body)
}

export const uploadNewFeatureMedia = async (formData) => {
  return instance.post('/new-features/media', formData)
}
